/**
 * axios配置文件
 * 可根据项目需求进行修改，只需更改该文件即可，其他文件无需改动
 */
import { nanoid } from 'nanoid';
import type { AxiosResponse } from 'axios';
import { showNotify } from 'vant';
import type { AxiosTransform, CreateAxiosOptions } from './axiosTransform';
import { VAxios } from './Axios';
import { useGlobSetting } from '@/hooks/setting';
import { ContentTypeEnum, RequestEnum } from '@/enums/httpEnum';
import { isString } from '@/utils/is';
import { deepMerge } from '@/utils';
import { setObjToUrlParams } from '@/utils/format';
import { useLoading } from '../../utils/useLoading';
import { clearToken, getToken } from '@/utils/auth';
import type { RequestOptions, Result } from 'types/axios';
import { formatRequestDate, isRequestCanceled, joinTimestamp } from './helper';
import router from '@/router';
import { teachRouteWhiteList } from '@/config/routes';

// 加载状态管理
const [openLoading, closeLoading] = useLoading();
/**
 * 显示错误消息（使用Vant的Notify组件）
 */
const showError = (message: string) => {
  showNotify({
    type: 'danger',
    message,
    duration: 3000,
  });
};

/**
 * 数据处理，方便区分多种处理方式
 */
const transform: AxiosTransform = {
  /**
   * 处理请求数据。如果数据不是预期格式，可直接抛出错误
   */
  transformRequestHook: (res: AxiosResponse<Result>, options: RequestOptions) => {
    const { isTransformResponse, isReturnNativeResponse } = options;

    // 是否返回原生响应头 比如：需要获取响应头时使用该属性
    if (isReturnNativeResponse) {
      closeLoading();
      return res;
    }
    // 不进行任何处理，直接返回
    // 用于页面代码可能需要直接获取code，data，message这些信息时开启
    if (!isTransformResponse) {
      closeLoading();
      return res.data;
    }

    // 错误的时候返回
    const { data } = res || {};
    if (!data && data !== 0) {
      showError('[HTTP] 请求无返回值');
      throw new Error('request error');
    }

    closeLoading();
    return data;
  },

  /**
   * 请求之前处理config
   */
  beforeRequestHook: (config, options) => {
    const globSetting = useGlobSetting();
    const urlPrefix = globSetting.urlPrefix;
    const { apiUrl, joinPrefix, joinParamsToUrl, formatDate, joinTime = true } = options;

    if (joinPrefix) {
      config.url = `${urlPrefix}${config.url}`;
    }

    if (apiUrl && isString(apiUrl)) {
      config.url = `${apiUrl}${config.url}`;
    }

    const params = config.params || {};
    const data = config.data || false;
    formatDate && data && !isString(data) && formatRequestDate(data);
    if (config.method?.toUpperCase() === RequestEnum.GET) {
      if (!isString(params)) {
        // 给 get 请求加上时间戳参数，避免从缓存中拿数据。
        config.params = Object.assign(params || {}, joinTimestamp(joinTime, false));
      } else {
        // 兼容restful风格
        config.url = config.url + params + `${joinTimestamp(joinTime, true)}`;
        config.params = undefined;
      }
    } else {
      if (!isString(params)) {
        formatDate && formatRequestDate(params);
        if (Reflect.has(config, 'data') && config.data && Object.keys(config.data).length > 0) {
          config.data = data;
          config.params = params;
        } else {
          // 非GET请求如果没有提供data，则将params视为data
          config.data = params;
          config.params = undefined;
        }
        if (joinParamsToUrl) {
          config.url = setObjToUrlParams(config.url as string, Object.assign({}, config.params, config.data));
        }
      } else {
        // 兼容restful风格
        config.url = config.url + params;
        config.params = undefined;
      }
    }
    console.log('config', config);
    return config;
  },

  /**
   * 请求拦截器处理
   */
  requestInterceptors: (config, _options) => {
    // 请求之前处理config
    if ((config as Recordable)?.withLoading !== false) {
      openLoading();
    }
    const token = getToken();
    if (token && (config as Recordable)?.requestOptions?.withToken !== false) {
      // 确保headers存在
      config.headers = config.headers || {};
      config.headers.Authorization = 'Coach ' + token;
    }
    const times = new Date().getTime();
    const requestId = nanoid(32);
    // 确保headers存在
    config.headers = config.headers || {};
    config.headers.Timestamp = times;
    config.headers.RequestId = requestId;
    // 从全局设置中获取 appId
    const globSetting = useGlobSetting();
    config.headers['Xi-App-Id'] = globSetting.appId;
    return config;
  },

  /**
   * 响应拦截器处理
   */
  responseInterceptors: (res: AxiosResponse<any>) => {
    const requestOptions: RequestOptions = (res?.config as CreateAxiosOptions)?.requestOptions || {};
    const errorMessageMode = requestOptions?.errorMessageMode;
    if (res.data.ErrCode) {
      switch (res.data.ErrCode) {
        case 10101:
          showError('账号或密码错误');
          break;
        case 1701:
          showError('串口通信超时');
          break;
        case 1702:
          showError('串口忙');
          break;
        case 1703:
          showError('串口请求数据错误');
          break;
        case 1705:
          showError('锁状态错误');
          break;
        case 1706:
          showError('mqtt消息过期');
          break;
        case 1707:
          showError('串口效验数据错误');
          break;
        default:
          if (errorMessageMode !== 'none') {
            showError(res.data.ErrCode + ' ' + res.data.ErrMsg);
          }
          break;
      }
    }
    closeLoading();
    return res;
  },

  /**
   * 响应错误处理
   */
  responseInterceptorsCatch: async (error: any) => {
    // 可以根据需要添加错误日志记录
    // const errorLogStore = useErrorLogStoreWithOut();
    // errorLogStore.addAjaxErrorInfo(error);
    console.log('responseInterceptors', error);

    const { response, message, config } = error || {};
    const errorMessageMode = config?.requestOptions?.errorMessageMode || 'none';
    const msg: string = response?.data?.message ?? '网络请求错误，请重试';
    const code: number = response?.data?.code;
    console.log('response', errorMessageMode, response);
    // 是否为取消的请求
    const isCancel = await isRequestCanceled(config.cancelToken);
    console.log('isCancel', isCancel);

    try {
      if (response && response.status === 403) {
        // 权限不足
        showError('账号已被禁用');
        // 清除账号缓存
        clearToken();
        closeLoading();
        window.location.reload();
        return Promise.reject(error);
      }

      // 账号密码错误
      if (response && response.status === 400) {
        closeLoading();
        return Promise.reject(error);
      }

      // 是否是教练端
      const teach = import.meta.env.VITE_APP_MODE === 'teach';
      // 对于助教token失效后跳回登录页面，用户的话直接刷新即可
      if (response && response.status === 401) {
        showError('token已失效,请重新登录');
        closeLoading();
        clearToken();

        // 如果当前已经在白名单页面（如促销页），则不触发强跳登录
        const currentPath = router.currentRoute.value.path;
        if (teach && teachRouteWhiteList.includes(currentPath)) {
          console.log('当前处于白名单页面，已清除token但不强制跳转');
          return Promise.reject(error);
        }

        if (teach) {
          // 教练端跳转到登录页
          router.replace({ path: '/login' });
        } else {
          // 普通用户刷新页面
          window.location.reload();
        }
        return Promise.reject(error);
      }

      if (response && response.status === 500) {
        if (errorMessageMode === 'modal') {
          // 使用Vant的Dialog替代ElementPlus的MessageBox
          showNotify({
            type: 'danger',
            message: code + ' ' + msg,
            duration: 5000,
          });
        } else if (errorMessageMode === 'notify') {
          showNotify({
            type: 'danger',
            message: code + ' ' + msg,
            duration: 5000,
          });
        } else if (errorMessageMode === 'message') {
          console.error('请求错误:', response);
          // 自定义错误信息
          if (response.data.ErrCode) {
            showError(response.data.ErrMsg);
            return response;
          }
        }
        // 以免none的时候没有任何提示
        showError(response.data.ErrMsg);
        closeLoading();
        return Promise.reject(error);
      }
    } catch (catchError) {
      // 如果是取消的，不用显示错误信息
      !isCancel && showError(message + catchError);
    }

    // 如果是取消的，不用显示错误信息
    !isCancel && showError(code + ' ' + msg + error);
    closeLoading();
    return Promise.reject(error);
  },
};

/**
 * 创建axios实例
 */
function createAxios(opt?: Partial<CreateAxiosOptions>) {
  const globSetting = useGlobSetting();

  return new VAxios(
    deepMerge(
      {
        // 认证方案
        authenticationScheme: '',
        appId: globSetting.appId,
        timeout: 60 * 1000,
        // 基础接口地址
        // baseURL: globSetting.apiUrl,
        // 接口可能会有通用的地址部分，可以统一抽取出来
        urlPrefix: globSetting.urlPrefix,
        headers: { 'Content-Type': ContentTypeEnum.JSON },
        // 如果是form-data格式
        // headers: { 'Content-Type': ContentTypeEnum.FORM_URLENCODED },
        // 数据处理方式
        transform,
        // 配置项，下面的选项都可以在独立的接口请求中覆盖
        requestOptions: {
          // 默认将prefix 添加到url
          joinPrefix: true,
          // 是否返回原生响应头 比如：需要获取响应头时使用该属性
          isReturnNativeResponse: false,
          // 需要对返回数据进行处理
          isTransformResponse: true,
          // post请求的时候添加参数到url
          joinParamsToUrl: false,
          // 格式化提交参数时间
          formatDate: true,
          // 消息提示类型
          errorMessageMode: 'message',
          // 接口地址
          apiUrl: globSetting.apiUrl,
          //  是否加入时间戳
          joinTime: false,
          // 忽略重复请求
          ignoreCancelToken: true,
          // 是否携带token
          withToken: true,
        },
      },
      opt || {},
    ),
  );
}

// 导出默认的http实例
export const defHttp = createAxios();
